/**
 * Private feature matrix at `/admin/feature-matrix`.
 * Admin-only marketing/demo asset: every shipped feature, grouped and
 * labeled by persona. Copy any persona (or everything) as clean
 * markdown, or print to PDF for distribution.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CrownIcon as Crown, HouseIcon as House, PawPrintIcon as PawPrint, ScissorsIcon as Scissors, DogIcon as Dog, GraduationCapIcon as GraduationCap, WrenchIcon as Wrench, TreeIcon as Tree, StorefrontIcon as Storefront, DeviceMobileIcon as DeviceMobile, GearIcon as Gear, CopyIcon as Copy, PrinterIcon as Printer, ArrowLeftIcon as ArrowLeft, LockKeyIcon as LockKey } from "@phosphor-icons/react";

const PERSONAS = [
  {
    id: "breeders",
    icon: Crown,
    color: "#B8860B",
    name: "Breeders & Kennel Owners",
    tagline: "Run the whole program — pedigrees, heat cycles, litters, buyers, and payments — from one place.",
    groups: [
      { title: "Registry & Pedigrees", items: [
        "Register dogs with official Armstrong K9 Registry certificates",
        "Multi-generation ancestry trees — pinch-to-zoom on mobile",
        "Litter registration with individual puppy records",
        "Ownership transfers that carry the dog's full history to the new family",
      ]},
      { title: "Breeding Tools", items: [
        "Heat-cycle tracking with predicted-heat push reminders",
        "Smart Matchmaker — data-driven stud & dam pairing suggestions",
        "Breeding directory listings with auto-renew",
        "Whelping-week countdown pushes so no due date sneaks up",
        "Go-home week reminders for every puppy in a litter",
      ]},
      { title: "Selling & Reputation", items: [
        "Puppy marketplace listings by breed and state",
        "A public kennel page — your program's home on the internet",
        "Buyer inquiries land in your in-app inbox with push alerts",
        "Want-to-Buy board — see active buyers searching for your breed",
        "Breeder reviews that build lasting reputation",
      ]},
      { title: "Growing the Program", items: [
        "Host and promote events — shows, meetups, litter reveals",
        "Social feed to showcase dogs and pick up followers",
        "Stripe-powered payments straight to your bank",
      ]},
    ],
  },
  {
    id: "families",
    icon: House,
    color: "#C1272D",
    name: "Puppy Buyers & New Families",
    tagline: "Find a verified breeder, dodge the scams, and count down to gotcha day.",
    groups: [
      { title: "Finding the Right Puppy", items: [
        "Browse available puppies by breed and state",
        "Directory of verified kennels with real pedigrees",
        "Post on the Want-to-Buy board and let breeders come to you",
      ]},
      { title: "Buying with Confidence", items: [
        "Verified multi-generation pedigrees — see exactly what you're getting",
        "Breeder reviews from real buyers",
        "Payments processed by Stripe — never wire money to a stranger",
      ]},
      { title: "Bringing Puppy Home", items: [
        "Go-home countdown pushes during the final week",
        "The dog's full registry record transfers to you automatically",
        "Vaccination booster and birthday reminders from day one",
      ]},
      { title: "Staying Connected", items: [
        "Follow your puppy's kennel for litter updates in your feed",
        "Chat with your breeder in-app — questions answered for life",
        "Local events to meet littermates and other families",
      ]},
    ],
  },
  {
    id: "owners",
    icon: PawPrint,
    color: "#2E6B34",
    name: "Everyday Dog Owners",
    tagline: "One app for your dog's whole life — health log, calendar, reminders, and trusted pros.",
    groups: [
      { title: "Dog Log & Health", items: [
        "One-tap daily logs — walk, meal, potty, meds, play",
        "Symptom tracking with automatic \"consider a vet\" alerts when patterns look off",
        "Vet appointment reminders",
      ]},
      { title: "Dog Care Passport", items: [
        "One care profile per dog — temperament, feeding, meds, quirks — filled once in ~60 seconds of tap-to-select chips",
        "Every groomer, walker, and sitter you book automatically sees the slice that matters for their job",
        "Pros can suggest passport notes after a visit (\"ate better with the slow feeder\") — you approve in one tap",
        "Pick which dog a booking is for and the passport rides along",
      ]},
      { title: "Calendar & Reminders", items: [
        "Every dog date in one calendar — tap a day to add anything",
        "One-tap sync to Google Calendar or Apple Calendar",
        "Vaccination boosters, birthdays, and medication reminders",
      ]},
      { title: "Booking Trusted Help", items: [
        "Find background-checked groomers, walkers, sitters, and trainers nearby",
        "Zoomie Time — rent a private, fenced field by the hour for safe off-leash zoomies; exact address unlocks after booking and visits never overlap",
        "Book and pay in-app; reschedule or cancel with fair refund rules",
        "Virtual video sessions when you can't get there in person",
      ]},
      { title: "Peace of Mind", items: [
        "Live GPS map while your dog is on a walk (with your consent)",
        "Mid-walk photos pushed straight to your lock screen",
        "Sitter updates while you're away — every walk, meal, and med logged",
      ]},
    ],
  },
  {
    id: "groomers",
    icon: Scissors,
    color: "#8A5CB8",
    name: "Groomers",
    tagline: "A storefront, a booking book, and a repeat-business machine — built for grooming.",
    groups: [
      { title: "Your Storefront", items: [
        "Public provider page with services, pricing, photos, and reviews",
        "Background-check badge that makes owners book with confidence",
        "Video tours so clients see your setup before they book",
      ]},
      { title: "Bookings & Payments", items: [
        "Instant online booking against your weekly availability",
        "Payments go directly to your bank via Stripe",
        "Recurring series, reschedules, and automatic session reminders",
      ]},
      { title: "The Wow Factor", items: [
        "Before & After report cards — photos + coat, nail, and ear grades pushed to the client's lock screen",
        "Personal notes on every groom (\"watch the left ear\") that clients actually read",
        "The dog's Care Passport shows coat type, matting spots, clipper anxiety, and bite history before the first appointment",
      ]},
      { title: "Repeat Business on Autopilot", items: [
        "Pick a rebook window (2–12 weeks) when you finish a groom",
        "Client gets a friendly \"time to rebook\" push with a one-tap link back to you",
        "They can add the suggested date to Google or Apple Calendar instantly",
        "Automatic review nudges after every completed appointment",
      ]},
    ],
  },
  {
    id: "walkers",
    icon: Dog,
    color: "#1D6FA5",
    name: "Dog Walkers & Sitters",
    tagline: "Show owners the walk as it happens — and get paid without chasing invoices.",
    groups: [
      { title: "Live Walk Experience", items: [
        "GPS live-tracking with owner consent — they watch the route in real time",
        "Walk start and finish pushes with distance & time recaps",
        "Snap mid-walk photos that push straight to the owner's lock screen",
        "Care Passport safety flags pinned on the walk screen — reactivity triggers, leash style, and gear at a glance",
      ]},
      { title: "Sitting Superpowers", items: [
        "Secure sitter link to the dog's care log — no shared passwords",
        "Log walks, meals, and meds; owner gets tasteful update pushes",
        "Serious symptoms automatically escalate to a vet-warning alert",
      ]},
      { title: "Business Basics", items: [
        "Public profile with reviews and background-check badge",
        "Recurring visit series with auto-charge — steady weekly income",
        "Direct Stripe payouts; the registry never holds your money",
      ]},
    ],
  },
  {
    id: "trainers",
    icon: GraduationCap,
    color: "#A34A00",
    name: "Trainers",
    tagline: "Know the dog before the first session, sell packages, and train in person or over video.",
    groups: [
      { title: "Getting Found", items: [
        "Provider directory listing by service and location",
        "Background check with a $25 credit back after your first completed booking",
        "Video tours to showcase your training style",
        "Care-match scores — owners see \"94% match for your dogs\" based on your preferences (reactive dogs, aggression & bite-history cases, puppies, seniors)",
        "\"Aggression cases accepted\" and \"Reactive dogs welcome\" badges right on your public profile",
        "Market snapshot: what other trainers in your state charge — you always set your own price",
      ]},
      { title: "Know the Dog Before Session One", items: [
        "The dog's Training & Behavior passport — goals, known commands, motivators, reactivity details, and bite/incident history — pinned to the top of every booking",
        "Owners are automatically nudged to fill it out when they book you (~1 minute)",
        "Safety flags surface bite and incident history so you can plan a safe session",
      ]},
      { title: "Virtual Sessions", items: [
        "Built-in video rooms — no Zoom links to email around",
        "Automatic 30-minute and 5-minute session reminders for both sides",
      ]},
      { title: "Running the Business", items: [
        "Recurring session packages with auto-charge, pause, and resume",
        "Reschedule tools with fair, automatic refund rules",
        "In-app chat with clients between sessions",
      ]},
    ],
  },
  {
    id: "other_pros",
    icon: Wrench,
    color: "#5B6D3A",
    name: "Specialty Service Pros (\"Other\")",
    tagline: "Pooper scoopers, pet photographers, massage therapists — if dogs need it, you can sell it here.",
    groups: [
      { title: "Your Own Category", items: [
        "List under your own custom specialty label — your service, your words",
        "Public provider page with services, pricing, photos, and reviews",
        "Background-check badge that makes owners book with confidence",
        "Care-match scores against each dog's Care Passport (puppies, seniors, reactive dogs)",
      ]},
      { title: "Bookings & Payments", items: [
        "Instant online booking against your weekly availability",
        "Clients book with their registered dogs attached — you always know who's coming",
        "Payments go directly to your bank via Stripe",
        "Recurring series, reschedules, and automatic reminders for both sides",
      ]},
      { title: "Growing the Business", items: [
        "In-app chat with clients",
        "Automatic review nudges after every completed booking",
        "Social feed to showcase your work and pick up followers",
      ]},
    ],
  },
  {
    id: "zoomie_hosts",
    icon: Tree,
    color: "#2E6B3E",
    name: "Zoomie Time Field Hosts",
    tagline: "Turn your unused land into income — rent your field by the hour for safe, private off-leash zoomies.",
    groups: [
      { title: "List Your Land", items: [
        "List your field in about 10 minutes — acreage, fencing status, photos, house rules",
        "Set your own hourly rate and availability windows",
        "Your exact address stays hidden — guests only see it after a confirmed, paid booking",
        "\"Reactive dogs welcome\" and \"Dogs in heat welcome\" badges that make your field the safe option",
      ]},
      { title: "Safe, Predictable Visits", items: [
        "Automatic buffer gaps between bookings — guests never overlap at your gate",
        "Every guest signs a liability waiver before checkout, recorded with a timestamp",
        "Guests book with their registered dogs attached — you see names and breeds before they arrive",
        "You approve the rules; guests agree to supervise, clean up, and leave on time",
      ]},
      { title: "Getting Paid", items: [
        "Payments go directly to your bank via Stripe — the registry never holds your money",
        "Booking dashboard with upcoming visits and cancellations",
        "Fair, automatic refund rules — weather doesn't refund unless you cancel",
      ]},
    ],
  },
  {
    id: "sellers",
    icon: Storefront,
    color: "#3E7C59",
    name: "Storefront Sellers",
    tagline: "Sell food, treats, and merch — with custom orders and reorders handled for you.",
    groups: [
      { title: "Your Store", items: [
        "Branded store page for food, treats, and merch",
        "New-order alerts by push, email, and in-app inbox — never miss a sale",
        "Order dashboard with one-tap \"mark delivered\"",
      ]},
      { title: "Custom Orders", items: [
        "Request → mockup → quote → pay flow, all in the platform",
        "Buyers get a push + email the moment their quote is ready",
        "Printify auto-fulfillment for merch — hands-free production",
      ]},
      { title: "Repeat Revenue", items: [
        "Tag products as consumables with your own reorder window (7–365 days)",
        "Buyers get a \"running low?\" push with a one-tap reorder link",
        "Delivered-order confirmations keep buyers in the loop",
      ]},
    ],
  },
  {
    id: "everyone",
    icon: DeviceMobile,
    color: "#0E0E0E",
    name: "Every Member",
    tagline: "The platform experience everyone gets, on web and in the native app.",
    groups: [
      { title: "Native iOS & Android App", items: [
        "Face ID / fingerprint login — one glance and you're in",
        "Native Google and Apple sign-in",
        "Stay signed in for up to 60 days",
        "20+ smart push notification triggers, all opt-out controllable",
      ]},
      { title: "Community & Discovery", items: [
        "Social feed with reactions",
        "Search across dogs, kennels, listings, and pros",
        "Events — create, RSVP, and discover",
        "Zoomie Time — landowners rent private fields by the hour; guests book safe, solo off-leash visits",
        "Real-time in-app messaging with a full inbox",
      ]},
      { title: "Trust & Safety", items: [
        "Background-checked service pros (Checkr)",
        "All payments via Stripe — the registry is the venue, never the middleman holding funds",
        "Click-through policy acknowledgements recorded on every sensitive transaction",
        "Scam & profanity screening on the feed AND private messages — bad content is blocked before it sends",
        "Block any member from a chat in one tap; report anything with reason-based severity",
        "Posts reported by 3 members auto-hide instantly, pending staff review",
      ]},
    ],
  },
  {
    id: "internal",
    icon: Gear,
    color: "#666666",
    name: "Platform Command Center",
    internal: true,
    tagline: "INTERNAL — your admin toolkit. Not for public distribution; great for investor demos.",
    groups: [
      { title: "Operations", items: [
        "Approvals queues for providers, storefronts, and applications",
        "Growth dashboard with persona funnels + saved growth playbook",
        "Analytics anomaly alerts and weekly ops digests",
        "Broadcast messaging to any member segment",
        "Storefront health monitor and reassignment tools",
        "High-severity reports (illegal/safety) page every admin's phone instantly",
        "Message mediation with quarantine and audit-logged access",
        "Funnel analytics: bookings, passports, report cards, registrations, store checkouts",
      ]},
    ],
  },
];

function personaMarkdown(p) {
  const lines = [`## ${p.name}`, p.tagline, ""];
  p.groups.forEach((g) => {
    lines.push(`### ${g.title}`);
    g.items.forEach((it) => lines.push(`- ${it}`));
    lines.push("");
  });
  return lines.join("\n");
}

function copyText(text, label) {
  const fallback = () => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      toast.success(`${label} copied — paste anywhere`);
    } catch {
      toast.error("Copy failed");
    }
    document.body.removeChild(ta);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied — paste anywhere`),
      fallback,
    );
  } else {
    fallback();
  }
}

function PersonaCard({ p }) {
  const Icon = p.icon;
  const count = p.groups.reduce((a, g) => a + g.items.length, 0);
  return (
    <section id={p.id} className="bg-white border border-[#EAEAEA] rounded-2xl p-5 sm:p-6 break-inside-avoid" data-testid={`persona-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}18` }}>
            <Icon size={22} weight="duotone" style={{ color: p.color }} />
          </span>
          <div>
            <h2 className="font-spectral text-xl sm:text-2xl tracking-tight flex items-center gap-2">
              {p.name}
              {p.internal && (
                <span className="text-[9px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-[#0E0E0E] text-white inline-flex items-center gap-1">
                  <LockKey size={9} weight="fill" /> Internal
                </span>
              )}
            </h2>
            <p className="text-[12.5px] text-[#6B6B6B]">{count} features</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-full print:hidden" onClick={() => copyText(personaMarkdown(p), p.name)} data-testid={`copy-${p.id}`}>
          <Copy size={13} className="mr-1" /> Copy this list
        </Button>
      </div>
      <p className="text-sm text-[#3F3F3F] mt-3 italic">{p.tagline}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4">
        {p.groups.map((g) => (
          <div key={g.title}>
            <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold mb-1.5" style={{ color: p.color }}>{g.title}</h3>
            <ul className="space-y-1">
              {g.items.map((it) => (
                <li key={it} className="text-[13.5px] text-[#3F3F3F] leading-snug pl-3 relative">
                  <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminFeatureMatrix() {
  const { user, loading } = useAuth();
  if (!loading && user?.role !== "admin") return <Navigate to="/" replace />;

  const total = PERSONAS.filter((p) => !p.internal).reduce(
    (a, p) => a + p.groups.reduce((x, g) => x + g.items.length, 0), 0);

  const copyAll = () => {
    const md = ["# Armstrong K9 Registry — Feature Matrix", ""]
      .concat(PERSONAS.filter((p) => !p.internal).map(personaMarkdown))
      .join("\n");
    copyText(md, "Full feature matrix");
  };

  return (
    <AppShell>
      <Helmet>
        <title>{"Feature matrix — Armstrong K9 Registry admin"}</title>
      </Helmet>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="print:hidden mb-2">
          <Link to="/admin/growth" className="inline-flex items-center gap-1 text-xs text-[#666] hover:text-ink" data-testid="matrix-back">
            <ArrowLeft size={12} /> Back to growth dashboard
          </Link>
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Admin · Private</p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">The Feature Matrix</h1>
            <p className="text-sm text-[#666] max-w-2xl mt-2">
              Every shipped feature, labeled by persona — {total} public-facing features and counting.
              Copy a persona for a pitch, or print the whole thing to PDF for handouts.
            </p>
          </div>
          <div className="flex gap-2 print:hidden">
            <Button variant="outline" className="rounded-full" onClick={copyAll} data-testid="matrix-copy-all">
              <Copy size={14} className="mr-1.5" /> Copy all
            </Button>
            <Button className="rounded-full bg-ink hover:bg-crimson text-white" onClick={() => window.print()} data-testid="matrix-print">
              <Printer size={14} className="mr-1.5" /> Print / PDF
            </Button>
          </div>
        </div>
        <nav className="print:hidden flex gap-2 flex-wrap my-4" data-testid="matrix-toc">
          {PERSONAS.map((p) => (
            <a key={p.id} href={`#${p.id}`}
              className="rounded-full px-3.5 py-1.5 text-[12px] font-semibold border border-[#E5E5E5] bg-white hover:border-ink transition-colors"
              style={{ color: p.color }}>
              {p.name}
            </a>
          ))}
        </nav>
        <div className="space-y-5">
          {PERSONAS.map((p) => <PersonaCard key={p.id} p={p} />)}
        </div>
        <p className="text-[11px] text-[#9A9A9A] mt-6 print:hidden">
          Private page — visible to admin accounts only. The "Internal" section is excluded from Copy All.
        </p>
      </div>
    </AppShell>
  );
}
